import { useState } from "react";
import type { OcpiLocation } from "../types/ocpi";
import {
  aggregateStatus,
  formatKw,
  locationHasAc,
  locationHasDc,
  locationMaxKw,
  locCoord,
  totalConnectors,
} from "../utils/format";
import { useI18n } from "../i18n/LanguageContext";
import { StatusPill } from "./StatusPill";
import { ConnectorBadge } from "./ConnectorBadge";
import { TariffBox } from "./TariffBox";

function buildDirectionsUrl(loc: OcpiLocation): string {
  const { lat, lng } = locCoord(loc);
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isApple = /iPhone|iPad|iPod|Mac OS X/.test(ua);
  if (isApple) {
    return `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
}

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

export function LocationDetail({
  location,
  distanceKm,
  onBack,
}: {
  location: OcpiLocation;
  distanceKm: number | null;
  onBack: () => void;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const kw = locationMaxKw(location);
  const status = aggregateStatus(location);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(`${location.name ?? location.id} — ${location.address}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* noop */
    }
  };

  return (
    <div className="flex h-full flex-col animate-fadeIn">
      <header className="sticky top-0 z-10 border-b border-ink-100 bg-white/95 px-4 py-3 backdrop-blur">
        <button
          onClick={onBack}
          className="mb-2 inline-flex items-center gap-1 text-[12px] font-medium text-ink-500 hover:text-ink-900"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {t("backToList")}
        </button>
        <h2 className="text-[16px] font-semibold text-ink-900 leading-tight">
          {location.name || location.id}
        </h2>
        <p className="mt-0.5 text-[12px] text-ink-500">{location.address}</p>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <StatusPill status={status} />
          {locationHasAc(location) && <span className="chip bg-brand-50 text-brand-700">{t("ac")}</span>}
          {locationHasDc(location) && <span className="chip bg-sky-50 text-sky-700">{t("dc")}</span>}
          <span className="chip bg-ink-50 text-ink-600">
            {totalConnectors(location)} {t("sockets")}
          </span>
          {distanceKm != null && (
            <span className="chip bg-ink-900 text-white tabular-nums">
              {formatDistance(distanceKm)}
            </span>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Primary CTAs */}
        <div className="flex items-center gap-2">
          <a
            href={buildDirectionsUrl(location)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary flex-1 !py-2"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M21.7 10.6L13.4 2.3a2 2 0 0 0-2.8 0L2.3 10.6a2 2 0 0 0 0 2.8l8.3 8.3a2 2 0 0 0 2.8 0l8.3-8.3a2 2 0 0 0 0-2.8zM10 9h4a3 3 0 0 1 0 6h-2v3l-3-3 3-3v3a1 1 0 0 0 0-2h-2V9z"
                fill="currentColor"
              />
            </svg>
            {t("directions")}
          </a>
          <button
            onClick={copyAddress}
            className="btn-ghost shrink-0 !py-2 border border-ink-100"
            title={t("copyAddress")}
          >
            {copied ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M5 12l5 5L20 7" stroke="#1B723B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t("copied")}
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <rect x="8" y="8" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
                  <rect x="4" y="4" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
                </svg>
                {t("copyAddress")}
              </>
            )}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Stat label={t("maxPower")} value={formatKw(kw)} />
          <Stat label={t("connectors")} value={String(totalConnectors(location))} />
        </div>

        <TariffBox location={location} />

        <section>
          <h3 className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-ink-700">
            {t("connectors")}
          </h3>
          <div className="space-y-2">
            {location.evses.map((evse) => (
              <div key={evse.uid} className="rounded-xl border border-ink-100 bg-white p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[11.5px] font-medium text-ink-500 tracking-wide">
                    EVSE · {evse.evse_id ?? evse.uid.slice(0, 8)}
                  </span>
                  <StatusPill status={evse.status} size="xs" />
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {evse.connectors.map((c) => (
                    <ConnectorBadge key={c.id} connector={c} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {location.facilities && location.facilities.length > 0 && (
          <section>
            <h3 className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-ink-700">
              {t("facilities")}
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {location.facilities.map((f) => (
                <span key={f} className="chip bg-ink-50 text-ink-600">
                  {t(("facility" + f) as never) ?? f}
                </span>
              ))}
            </div>
          </section>
        )}

        {location.operator?.name && (
          <section className="text-[12px] text-ink-500">
            <span className="font-medium text-ink-700">{t("operator")}:</span>{" "}
            {location.operator.name}
          </section>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-ink-100 bg-white p-3">
      <div className="text-[10.5px] font-medium uppercase tracking-wide text-ink-400">
        {label}
      </div>
      <div className="mt-0.5 text-[17px] font-semibold tabular-nums text-ink-900">
        {value}
      </div>
    </div>
  );
}
